import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { db } from '../../db';
import stravaApi from '../../services/stravaApi';
import { refreshStravaToken } from '../auth/AuthSlice';

export const loadRoutes = createAsyncThunk(
    "routeSelection/loadRoutes",
    async ({ pageNum = 1, search }, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState();
            const isOnline = navigator.onLine;

            // Helper function to add cache status to routes
            const addCacheStatusToRoutes = (routes, cachedRoutes) => {
                const cachedRouteIds = new Set(cachedRoutes.map(route => {
                    const routeData = route?.routeJson || {};
                    return String(routeData.id || route.id.split(':').pop() || route.id);
                }));

                return routes.map(route => {
                    const routeId = String(route.id);
                    const cachedRoute = cachedRoutes.find(cachedRoute => {
                        const cachedRouteData = cachedRoute?.routeJson || {};
                        const cachedRouteId = cachedRouteData.id || cachedRoute.id.split(':').pop() || cachedRoute.id;
                        return String(cachedRouteId) === routeId;
                    });
                    return {
                        ...route,
                        id: routeId,
                        isCached: cachedRouteIds.has(routeId),
                        cachedAt: cachedRoute?.cachedAt || null
                    };
                });
            };

            // Helper function to load Strava routes
            const loadStravaRoutes = async () => {
                let stravaAuth = state.auth.connections.strava;

                if (!stravaAuth) {
                    return { routes: [], source: 'strava' };
                }

                // Check if token needs refresh and refresh if necessary
                if (stravaApi.isTokenExpired(stravaAuth)) {
                    try {
                        const refreshResult = await dispatch(refreshStravaToken());
                        if (refreshResult.type === 'auth/refreshStravaToken/fulfilled') {
                            stravaAuth = refreshResult.payload;
                        } else {
                            throw new Error('Failed to refresh Strava token');
                        }
                    } catch (error) {
                        throw new Error('Strava token expired and could not be refreshed');
                    }
                }

                if (!isOnline) {
                    const cachedRoutes = await db.routes.toArray();
                    const stravaRoutes = cachedRoutes
                        .filter(route => route?.routeJson?.source === 'strava')
                        .map(route => {
                            const routeData = route?.routeJson || {};
                            return {
                                id: routeData.id || route.id.split(':').pop() || route.id,
                                cachedAt: route.cachedAt,
                                ...routeData,
                                // ensure required fields exist for offline rendering (set AFTER spread)
                                elevation_gain: (routeData && typeof routeData.elevation_gain === 'number') ? routeData.elevation_gain : 0,
                                created_at: routeData?.created_at || route.cachedAt || new Date().toISOString(),
                                isCached: true,
                                source: 'strava'
                            };
                        });
                    return { routes: stravaRoutes, source: 'strava' };
                }

                const stravaToken = stravaAuth.access_token;
                const response = await stravaApi.getRoutes(stravaToken, pageNum, 200);
                const stravaRoutes = response.routes.map(route => stravaApi.mapStravaRoute(route));

                const cachedRoutes = await db.routes.toArray();
                const stravaRoutesWithCacheStatus = addCacheStatusToRoutes(stravaRoutes, cachedRoutes).map(route => ({
                    ...route,
                    source: 'strava'
                }));

                return { routes: stravaRoutesWithCacheStatus, source: 'strava' };
            };

            // Helper function to load RWGPS routes
            const loadRWGPSRoutes = async () => {
                const rwgpsAuth = state.auth.connections.rwgps;
                const token = rwgpsAuth?.access_token;

                if (!token) {
                    return { routes: [], source: 'rwgps' };
                }

                if (!isOnline) {
                    const cachedRoutes = await db.routes.toArray();
                    const rwgpsRoutes = cachedRoutes
                        .filter(route => {
                            const routeData = route?.routeJson || {};
                            return !routeData.source || routeData.source === 'rwgps';
                        })
                        .map(route => {
                            const routeData = route?.routeJson || {};
                            return {
                                id: routeData.id || route.id.split(':').pop() || route.id,
                                cachedAt: route.cachedAt,
                                ...routeData,
                                elevation_gain: (routeData && typeof routeData.elevation_gain === 'number') ? routeData.elevation_gain : 0,
                                created_at: routeData?.created_at || route.cachedAt || new Date().toISOString(),
                                isCached: true,
                                source: 'rwgps'
                            };
                        });
                    return { routes: rwgpsRoutes, source: 'rwgps' };
                }

                try {
                    const response = await fetch(`https://ridewithgps.com/api/v1/routes.json?page=1&limit=50`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        }
                    });
                    const data = await response.json();

                    if (data.routes && data.routes.length > 0) {
                        const cachedRoutes = await db.routes.toArray();
                        const firstPageRoutes = addCacheStatusToRoutes(data.routes.map(route => ({
                            ...route,
                            id: String(route.id),
                            source: 'rwgps'
                        })), cachedRoutes);

                        // Start loading remaining pages in background
                        (async () => {
                            let currentPage = 2;
                            let hasMoreRoutes = true;
                            let allFetchedRoutes = [...firstPageRoutes];

                            while (hasMoreRoutes) {
                                try {
                                    const response = await fetch(`https://ridewithgps.com/api/v1/routes.json?page=${currentPage}&limit=50`, {
                                        method: 'GET',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${token}`,
                                        }
                                    });
                                    const data = await response.json();

                                    if (data.routes && data.routes.length > 0) {
                                        const newRoutes = addCacheStatusToRoutes(data.routes.map(route => ({
                                            ...route,
                                            id: String(route.id),
                                            source: 'rwgps'
                                        })), cachedRoutes);
                                        allFetchedRoutes = [...allFetchedRoutes, ...newRoutes];
                                        dispatch(updateAllRoutes(allFetchedRoutes));
                                        currentPage++;
                                    } else {
                                        hasMoreRoutes = false;
                                    }
                                } catch (error) {
                                    console.error('Failed to fetch additional routes:', error);
                                    hasMoreRoutes = false;
                                }
                            }
                        })();

                        // Merge with any cached routes that aren't in the user's account
                        const userRouteIds = new Set(firstPageRoutes.map(route => String(route.id)));
                        const additionalCachedRoutes = cachedRoutes
                            .filter(route => {
                                const routeData = route?.routeJson || {};
                                const routeId = routeData.id || route.id.split(':').pop() || route.id;
                                return !userRouteIds.has(String(routeId));
                            })
                            .map(route => {
                                const routeData = route?.routeJson || {};
                                return {
                                    id: routeData.id || route.id.split(':').pop() || route.id,
                                    cachedAt: route.cachedAt,
                                    ...routeData,
                                    isCached: true,
                                    isPublicRoute: false,
                                    source: routeData.source || 'rwgps'
                                };
                            });

                        return {
                            routes: [...firstPageRoutes, ...additionalCachedRoutes],
                            source: 'rwgps'
                        };
                    }
                } catch (error) {
                    console.error('Failed to fetch RWGPS routes:', error);
                    throw error;
                }

                return { routes: [], source: 'rwgps' };
            };

            // If offline, load cached routes from all sources
            if (!isOnline) {
                const cachedRoutes = await db.routes.toArray();
                const routesWithCacheStatus = cachedRoutes.map(route => {
                    const routeData = route?.routeJson || {};
                    return {
                        id: routeData.id || route.id.split(':').pop() || route.id,
                        cachedAt: route.cachedAt,
                        ...routeData,
                        elevation_gain: (routeData && typeof routeData.elevation_gain === 'number') ? routeData.elevation_gain : 0,
                        created_at: routeData?.created_at || route.cachedAt || new Date().toISOString(),
                        isCached: true,
                        source: routeData.source || 'rwgps'
                    };
                });

                return {
                    data: { routes: routesWithCacheStatus },
                    allRoutes: routesWithCacheStatus,
                    current_server_page: pageNum,
                    search: search || ''
                };
            }

            // Load routes from all connected sources concurrently
            const loadPromises = [];

            if (state.auth.connections.rwgps?.access_token) {
                loadPromises.push(loadRWGPSRoutes().catch(error => {
                    console.error('Error loading RWGPS routes:', error);
                    return { routes: [], source: 'rwgps' };
                }));
            }

            if (state.auth.connections.strava) {
                loadPromises.push(loadStravaRoutes().catch(error => {
                    console.error('Error loading Strava routes:', error);
                    return { routes: [], source: 'strava' };
                }));
            }

            // If no connections, fall back to cached routes
            if (loadPromises.length === 0) {
                const cachedRoutes = await db.routes.toArray();
                const routesWithCacheStatus = cachedRoutes.map(route => {
                    const routeData = route?.routeJson || {};
                    return {
                        id: routeData.id || route.id.split(':').pop() || route.id,
                        cachedAt: route.cachedAt,
                        ...routeData,
                        elevation_gain: (routeData && typeof routeData.elevation_gain === 'number') ? routeData.elevation_gain : 0,
                        created_at: routeData?.created_at || route.cachedAt || new Date().toISOString(),
                        isCached: true,
                        source: routeData.source || 'rwgps'
                    };
                });

                return {
                    data: { routes: routesWithCacheStatus },
                    allRoutes: routesWithCacheStatus,
                    current_server_page: pageNum,
                    search: search || ''
                };
            }

            const results = await Promise.all(loadPromises);

            // Merge all routes together, avoiding duplicates
            const allLoadedRoutes = [];
            const routeIdsSeen = new Set();

            for (const result of results) {
                for (const route of result.routes) {
                    const routeId = String(route.id);
                    if (!routeIdsSeen.has(routeId)) {
                        routeIdsSeen.add(routeId);
                        allLoadedRoutes.push(route);
                    }
                }
            }

            // Merge with any additional cached routes that weren't loaded
            const cachedRoutes = await db.routes.toArray();
            const loadedRouteIds = new Set(allLoadedRoutes.map(route => String(route.id)));
            const additionalCachedRoutes = cachedRoutes
                .filter(route => {
                    const routeData = route?.routeJson || {};
                    const routeId = routeData.id || route.id.split(':').pop() || route.id;
                    return !loadedRouteIds.has(String(routeId));
                })
                .map(route => {
                    const routeData = route?.routeJson || {};
                    return {
                        id: routeData.id || route.id.split(':').pop() || route.id,
                        cachedAt: route.cachedAt,
                        ...routeData,
                        elevation_gain: (routeData && typeof routeData.elevation_gain === 'number') ? routeData.elevation_gain : 0,
                        created_at: routeData?.created_at || route.cachedAt || new Date().toISOString(),
                        isCached: true,
                        isPublicRoute: false,
                        source: routeData.source || 'rwgps'
                    };
                });

            const allRoutesWithCache = [...allLoadedRoutes, ...additionalCachedRoutes];

            return {
                data: { routes: allRoutesWithCache },
                allRoutes: allRoutesWithCache,
                current_server_page: pageNum,
                search: search || ''
            };

        } catch (error) {
            console.error('Error loading routes:', error);
            return rejectWithValue(error.message);
        }
    });

export const removeFromCache = createAsyncThunk(
    'routeSelection/removeFromCache',
    async (routeId, { getState }) => {
        // Try to find the route to get its source
        const allRoutes = getState().routeSelection.allRoutes;
        const route = allRoutes.find(r => String(r.id) === String(routeId));
        const source = route?.source;

        // Delete from IndexedDB - try both old format and new format
        const cacheKey = source ? `${source}:${routeId}` : String(routeId);
        try {
            await db.routes.delete(cacheKey);
        } catch (e) {
            // Also try old format for backward compatibility
            try {
                await db.routes.delete(String(routeId));
            } catch (e2) {
                console.warn('Failed to delete route from cache:', e2);
            }
        }

        // Also try deleting by routeId if cacheKey is different
        if (cacheKey !== String(routeId)) {
            try {
                await db.routes.delete(String(routeId));
            } catch (e) {
                // Ignore if already deleted
            }
        }

        // Delete from service worker cache
        if ('serviceWorker' in navigator) {
            const cache = await caches.open('cuemeup-v1');
            // Delete both RWGPS and Strava API URLs for the route
            await cache.delete(`https://ridewithgps.com/routes/${routeId}.json`);
            // Strava routes might be cached differently, so we check if the route is Strava
            if (source === 'strava') {
                // Strava API responses might be cached - delete if exists
                // Note: Strava GPX export endpoint might vary
                const stravaCacheKeys = await cache.keys();
                stravaCacheKeys.forEach(request => {
                    if (request.url.includes(`routes/${routeId}`)) {
                        cache.delete(request);
                    }
                });
            }
        }
        return { routeId };
    }
);


export const routeSelectionSlice = createSlice({
    name: 'routeSelection',
    initialState: {
        routeId: '',
        allRoutes: [], // Store all routes
        filteredRoutes: [], // Store search results
        current_server_page: 1,
        current_search: '',
        filters: {
            cacheStatus: 'all', // 'all', 'cached', 'uncached',
            provider: 'all', // 'all', 'rwgps', 'strava', 'gpx'
            // Add more filters here in the future
        },
    },
    reducers: {
        setRoute(state, action) {
            state.routeId = action.payload;
        },
        searchRoutes(state, action) {
            const searchTerm = action.payload.toLowerCase();
            if (!searchTerm) {
                state.filteredRoutes = applyFilters(state.allRoutes, state.filters);
                state.current_search = '';
                return;
            }

            let filtered = state.allRoutes.filter(route =>
                route.name.toLowerCase().includes(searchTerm) ||
                route.description?.toLowerCase().includes(searchTerm) ||
                route.tag_list?.toLowerCase().includes(searchTerm)
            );

            // Apply active filters to search results
            filtered = applyFilters(filtered, state.filters);

            state.filteredRoutes = filtered;
            state.current_search = searchTerm;
            state.current_server_page = 1; // Reset to first page when searching
        },
        updateFilter(state, action) {
            const { filterType, value } = action.payload;
            state.filters[filterType] = value;

            let filtered = state.allRoutes;

            // Apply search filter first if there's a search term
            if (state.current_search) {
                const searchTerm = state.current_search.toLowerCase();
                filtered = filtered.filter(route =>
                    route.name.toLowerCase().includes(searchTerm) ||
                    route.description?.toLowerCase().includes(searchTerm) ||
                    route.tag_list?.toLowerCase().includes(searchTerm)
                );
            }

            // Apply all active filters
            filtered = applyFilters(filtered, state.filters);

            state.filteredRoutes = filtered;
        },
        updateRouteCacheStatus(state, action) {
            const { routeId, cachedAt } = action.payload;
            state.allRoutes = state.allRoutes.map(route =>
                String(route.id) === String(routeId)
                    ? { ...route, isCached: true, cachedAt }
                    : route
            );
            state.filteredRoutes = state.filteredRoutes.map(route =>
                String(route.id) === String(routeId)
                    ? { ...route, isCached: true, cachedAt }
                    : route
            );
        },
        removeRouteFromCache(state, action) {
            const { routeId } = action.payload;
            state.allRoutes = state.allRoutes.map(route =>
                String(route.id) === String(routeId)
                    ? { ...route, isCached: false, cachedAt: null }
                    : route
            );
            state.filteredRoutes = state.filteredRoutes.map(route =>
                String(route.id) === String(routeId)
                    ? { ...route, isCached: false, cachedAt: null }
                    : route
            );
        },
        updateAllRoutes(state, action) {
            // Deduplicate routes by ID to prevent duplicates
            const routeMap = new Map();
            action.payload.forEach(route => {
                if (!routeMap.has(route.id)) {
                    routeMap.set(route.id, route);
                }
            });
            const deduplicatedRoutes = Array.from(routeMap.values());
            state.allRoutes = deduplicatedRoutes;
            state.filteredRoutes = applyFilters(deduplicatedRoutes, state.filters);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadRoutes.fulfilled, (state, action) => {
                // Deduplicate routes by ID to prevent duplicates
                const routeMap = new Map();
                action.payload.allRoutes.forEach(route => {
                    if (!routeMap.has(route.id)) {
                        routeMap.set(route.id, route);
                    }
                });
                state.allRoutes = Array.from(routeMap.values());
                state.current_server_page = action.payload.current_server_page;
                state.current_search = action.payload.search || '';
                // Apply filters to initial load
                state.filteredRoutes = applyFilters(state.allRoutes, state.filters);
            })
            .addCase(loadRoutes.rejected, (state, action) => {
                console.error("load routes failed:", action.payload);
            })
            .addCase(removeFromCache.fulfilled, (state, action) => {
                const { routeId } = action.payload;
                const route = state.allRoutes.find(r => String(r.id) === String(routeId));

                // For GPX routes, remove them completely from the list
                if (route?.source === 'gpx') {
                    state.allRoutes = state.allRoutes.filter(route =>
                        String(route.id) !== String(routeId)
                    );
                    state.filteredRoutes = state.filteredRoutes.filter(route =>
                        String(route.id) !== String(routeId)
                    );
                } else {
                    // For other routes, just mark as uncached
                    state.allRoutes = state.allRoutes.map(route =>
                        String(route.id) === String(routeId)
                            ? { ...route, isCached: false, cachedAt: null }
                            : route
                    );
                    state.filteredRoutes = state.filteredRoutes.map(route =>
                        String(route.id) === String(routeId)
                            ? { ...route, isCached: false, cachedAt: null }
                            : route
                    );
                }
            });
    },
})

// Helper function to apply all filters
function applyFilters(routes, filters) {
    let filtered = routes;

    // Apply cache status filter
    if (filters.cacheStatus === 'cached') {
        filtered = filtered.filter(route => route.isCached);
    } else if (filters.cacheStatus === 'uncached') {
        filtered = filtered.filter(route => !route.isCached);
    }

    // Apply provider filter
    if (filters.provider === 'rwgps') {
        filtered = filtered.filter(route => route.source === 'rwgps');
    } else if (filters.provider === 'strava') {
        filtered = filtered.filter(route => route.source === 'strava');
    } else if (filters.provider === 'gpx') {
        filtered = filtered.filter(route => route.source === 'gpx');
    }

    return filtered;
}

// Action creators are generated for each case reducer function
export const {
    setRoute,
    searchRoutes,
    updateFilter,
    updateRouteCacheStatus,
    removeRouteFromCache,
    updateAllRoutes,
} = routeSelectionSlice.actions

export default routeSelectionSlice.reducer
